<template>
	<div :class="{'page-fixed-height': !activePanel}">
		<div class="sideways-page-one" :class="{'disabled': !activePanel}">
			<!-- BEGIN PAGE BAR -->
			<div class="page-bar">
				<breadcrumb :crumbs="breadcrumbArray"></breadcrumb>
			</div>
			<!-- END PAGE BAR -->
			<!-- BEGIN PAGE TITLE-->
			<h1 class="page-title">Regions</h1>
			<!-- END PAGE TITLE-->
			<div class="note note-info">
				Create and manage your business regions.
			</div>
			<button v-if="noRegions" type="button" class="btn blue btn-lg" @click="throttledCreate()">Create Your First Region</button>
			<div class="margin-top-40">
				<div class="relative-block">
					<!-- REGIONS TREE START -->
					<loading-screen :show="loadingRegionData" :color="'#3598dc'" :display="'inline'"></loading-screen>
					<div v-if="loadingRegionData === false">
						<span class="caption-subject font-blue bold uppercase" v-if="regionPath.length">Regions in {{ regionPath[regionPath.length - 1].name }}</span>
						<div class="mt-element-list">
							<div class="mt-list-container list-simple ext-1 group no-border">
								<a class="list-toggle-container" v-if="regionPath.length" @click="goUpOneLevel()">
									<div class="list-toggle not-selected">
										<span class="fa fa-chevron-left margin-right-10"></span>
										Go back to {{ regionPath.length > 1 ? regionPath[regionPath.length - 2].name : 'global regions' }}
									</div>
								</a>
								<template v-for="region in computedRegions">
									<a class="list-toggle-container">
										<div class="list-toggle uppercase" :class="{'not-selected': currentRegion.id !== region.id}" @click="drillDown(region)">
											{{ region.name }}
											<span v-if="region.has_children" class="pull-right fa fa-chevron-right"></span>
											<dropdown class="pull-right margin-right-10 skinny-dropdown" :class="{'margin-right-25': !region.has_children}">
												<button slot="button" type="button" class="btn btn-sm btn-outline dropdown-toggle btn-default" data-toggle="dropdown" @click.stop>
													<i class="fa fa-cogs"></i>
												</button>
												<ul slot="dropdown-menu" class="dropdown-menu" @click.stop>
													<li v-if="!region.has_children">
														<a @click="createRegion(region.id)">
															Add children to <b>{{ region.name }}</b>
														</a>
													</li>
													<li>
														<a @click="manageLocations(region.id)">
															Manage <b>{{ region.name }}</b> locations
														</a>
													</li>
												</ul>
											</dropdown>
										</div>
									</a>
								</template>
							</div>
						</div>
						<div class="margin-top-20">
							<button type="button" class="btn blue" v-if="regionPath.length" @click="createRegion(regionPath[regionPath.length - 1].id)">Add region in {{ regionPath[regionPath.length - 1].name }}</button>
						</div>
					</div>
					<!-- REGIONS TREE END -->
				</div>
			</div>
		</div>

		<!-- PANEL COMPONENTS START -->
		<div class="sideways-page-two" :class="{'active': !activePanel, 'inactive': activePanel}" :style="{height: computedHeight}">
			<div class="sideways-blank-fold">
	    		<div class="text-center">
	    			<button type="button" class="btn blue btn-lg margin-bottom-10" @click="closePanel()">Return to this page</button>
	    		</div>
	    	</div>
	    	<create-region></create-region>
	    	<manage-locations></manage-locations>
		</div>
		<!-- PANEL COMPONENTS END -->

	</div>
</template>
<script>
import $ from 'jquery'
import Breadcrumb from '../../modules/Breadcrumb'
import Dropdown from '../../modules/Dropdown'
import LoadingScreen from '../../modules/LoadingScreen'
import { throttle, debounce } from 'lodash'
import Region from '../../../controllers/Region'
import CreateRegion from './Regions/CreateRegion'
import ManageLocations from './Regions/ManageLocations'
import { mapGetters } from 'vuex'

/**
 * Regions module is a page for the regions dashboard and creation process.
 * @class Regions
 * @since 0.0.8
 */

export default {
	data () {
		return {
			breadcrumbArray: [
				{name: 'My Business', link: false},
				{name: 'Regions', link: false}
			],
			activePanel: true,
			computedHeight: 0,
			regions: [],
			currentRegion: {},
			regionPath: [],
			noRegions: false,
			loadingRegionData: false
		}
	},
	/**
	 * Run on `created` to initialize the throttle events and to check the current process step.
	 * @function
	 * @returns {undefined}
	 * @memberof Regions
	 * @version 0.0.8
	 */
	created () {
		this.throttledCreate = throttle(() => {
			this.createRegion()
		}, 500, { 'trailing': false })

		if (this.$route.name !== 'My Business Regions') {
			this.activePanel = false
		}

		let _this = this

		this.computeHeight()

		$(window).on('resize', debounce(() => {
			_this.computeHeight()
		}, 200))

		setTimeout(() => { this.getRegions() }, 100)
	},
	computed: {
		/**
		 * To set the regions showing at the current level of the tree.
		 * @function
		 * @returns {array} - An array of the current regions to show
		 * @memberof Regions
		 * @version 0.0.8
		 */
		computedRegions () {
			if (!this.regionPath.length) {
				return this.regions
			} else {
				return this.regionPath[this.regionPath.length - 1].children
			}
		},
		...mapGetters([
			'activeBusinessId'
		])
	},
	methods: {
		/**
		 * To compute height to display based on window height and navbar
		 * @function
		 * @returns {undefined}
		 * @memberof Regions
		 * @version 0.0.8
		 */
		computeHeight () {
			const height = $(window).height() - 50
			this.computedHeight = height + 'px'
		},
		/**
		 * To close anything active in the side panel by redirecting to the region dashboard route
		 * @function
		 * @returns {undefined}
		 * @memberof Regions
		 * @version 0.0.8
		 */
		closePanel () {
			this.$router.push('/app/my_business/regions')
		},
		/**
		 * To initialize the create region process by redirecting to the create region route
		 * @function
		 * @param {integer} parentId - If this region is being created as a child then this will be the parent id of the new region.
		 * @returns {undefined}
		 * @memberof Regions
		 * @version 0.0.8
		 */
		createRegion (parentId) {
			if (parentId) {
				this.$router.parent_id = parentId
				this.$router.push('/app/my_business/regions/create_region')
			}
		},
		manageLocations (parentId) {
			this.$router.push('/app/my_business/regions/manage_locations/' + parentId)
		},
		/**
		 * To get a list of child regions under the current parent.
		 * @function
		 * @param {integer} parentId - The parent region that we are retrieving results for.
		 * @returns {object} - A promise that will either return an error message or perform an action.
		 * @memberof Regions
		 * @version 0.0.8
		 */
		getRegions (parentId) {
			this.loadingRegionData = true

			let _this = this
			const businessId = this.activeBusinessId

			return Region.getRegions(businessId, parentId).then(response => {
				if (parentId) {
					let i = 0
					let runningRegion = {}
					while (i < _this.regionPath.length) {
						if (i > 0) {
							runningRegion.children.forEach(region => {
								if (region.id === _this.regionPath[i].id) {
									if (region.id === parentId) {
										region.children = response.payload.regions
										_this.regionPath.splice(i, 1, region)
									} else {
										runningRegion = region
									}
								}
							})
						} else {
							_this.regions.forEach(region => {
								if (region.id === _this.regionPath[i].id) {
									if (region.id === parentId) {
										region.children = response.payload.regions
										_this.regionPath.splice(i, 1, region)
									} else {
										runningRegion = region
									}
								}
							})
						}
						i += 1
					}
				} else {
					_this.regions = response.payload.regions
				}
				_this.loadingRegionData = false
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'business_not_found':
						_this.$swal({
							title: 'Error',
							html: 'There is an issue with your business account. Please contact your admin.',
							type: 'warning'
						})
						break

					case 'invalid_region':
						break

					case 'regions_not_found':
						if (!parentId) {
							_this.noRegions = true
						}
						break

					case 'parent_region_not_found':
						break

					default:
						// No default
					}
				}
				_this.loadingRegionData = false
			}))
		},
		/**
		 * To set the current region and drill down into it's children.
		 * @function
		 * @param {object} region - The current region that we are drilling into.
		 * @returns {undefined}
		 * @memberof Regions
		 * @version 0.0.8
		 */
		drillDown (region) {
			if (region.has_children) {
				this.regionPath.push(region)
				this.getRegions(region.id)
			}
		},
		/**
		 * To move up a level in the tree.
		 * @function
		 * @returns {undefined}
		 * @memberof Regions
		 * @version 0.0.8
		 */
		goUpOneLevel () {
			this.regionPath.pop()
		}
	},
	components: {
		Breadcrumb,
		Dropdown,
		LoadingScreen,
		CreateRegion,
		ManageLocations
	},
	watch: {
		/**
		 * Watch `$route` so that we can check if we are on the proper panel after each change.
		 * @function
		 * @returns {undefined}
		 * @memberof Regions
		 * @version 0.0.8
		 */
		'$route' () {
			if (this.$route.name !== 'My Business Regions') {
				this.activePanel = false
			} else {
				this.activePanel = true
				this.getRegions(this.regionPath[this.regionPath.length - 1].id)
			}
		}
	}
}
</script>
<style>
.dd-item > button.button-spacer:before {
	content: '';
}
.skinny-dropdown .btn:not(.md-skip):not(.bs-select-all):not(.bs-deselect-all).btn-sm {
	padding: 1px 18px;
}
.skinny-dropdown {
	text-transform: none;
}
</style>



// WEBPACK FOOTER //
// Regions.vue?7bb21b45